// src/pages/ShopProfile.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { firestore, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import DeleteIcon from "@mui/icons-material/Delete";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const ShopProfile = () => {
  const auth = getAuth();
  const [user, loadingAuth] = useAuthState(auth);
  // Initialize profile with operatingHours as an object with empty arrays for each day.
  const [profile, setProfile] = useState({
    name: "",
    address: { street: "", city: "", postalCode: "", country: "" },
    location: { lat: "", lng: "" },
    fullAddress: "",
    imageUrl: "",
    slug: "",
    description: "",
    phone: "",
    operatingHours: {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    },
    dropOffLocations: [],
    deliveryToHotel: {
      active: false,
      price: "",
    },
  });

  const [imageFile, setImageFile] = useState(null);
  const [slugError, setSlugError] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Use authenticated user's UID as shopId once loaded
  const shopId = user ? user.uid : null;

  // Fetch the shop profile from Firestore once the user is authenticated
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      try {
        const ownerDocRef = doc(firestore, "users", user.uid);
        const ownerSnap = await getDoc(ownerDocRef);
        if (!ownerSnap.exists())
          throw new Error("No shop assigned to this user.");
        const shopSlug = ownerSnap.data().shopId;

        const shopDocRef = doc(firestore, "rentalShops", shopSlug);
        const shopSnap = await getDoc(shopDocRef);

        const bankDetailsDocRef = doc(
          firestore,
          "rentalShops",
          shopSlug,
          "private",
          "bankDetails"
        );
        const bankSnap = await getDoc(bankDetailsDocRef);

        if (shopSnap.exists()) {
          const shopData = shopSnap.data();
          const bankData = bankSnap.exists()
            ? bankSnap.data()
            : {
                bankName: "",
                accountName: "",
                accountNumber: "",
              };

          setProfile({
            name: shopData.name || "",
            address: {
              street: shopData.address?.street || "",
              city: shopData.address?.city || "",
              postalCode: shopData.address?.postalCode || "",
              country: shopData.address?.country || "",
            },
            fullAddress: shopData.fullAddress || "",
            location: {
              lat: shopData.location?.lat || "",
              lng: shopData.location?.lng || "",
            },
            imageUrl: shopData.imageUrl || "",
            slug: shopData.slug || "",
            description: shopData.description || "",
            phone: shopData.phone || "",
            operatingHours: shopData.operatingHours || {
              Monday: [],
              Tuesday: [],
              Wednesday: [],
              Thursday: [],
              Friday: [],
              Saturday: [],
              Sunday: [],
            },
            dropOffLocations: shopData.dropOffLocations || [],
            deliveryToHotel: shopData.deliveryToHotel || {
              active: false,
              price: "",
            },
            bankDetails: bankData,
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  const handleDropOffChange = (index, field, value) => {
    setProfile((prev) => {
      const newLocations = [...prev.dropOffLocations];
      newLocations[index] = { ...newLocations[index], [field]: value };
      return { ...prev, dropOffLocations: newLocations };
    });
  };

  const handleAddDropOff = () => {
    setProfile((prev) => ({
      ...prev,
      dropOffLocations: [
        ...prev.dropOffLocations,
        {
          locationName: "",
          address: "",
          price: "",
          blockingTimeAfterDropoff: "",
        },
      ],
    }));
  };

  const handleRemoveDropOff = (index) => {
    setProfile((prev) => {
      const updated = prev.dropOffLocations.filter((_, i) => i !== index);
      return { ...prev, dropOffLocations: updated };
    });
  };

  // Handle text input changes for basic profile fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
    if (name === "slug") {
      setSlugError("");
    }
  };

  // Handle file input for a new shop image
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [name]: parseFloat(value),
      },
    }));
  };

  // Check if a given slug is already taken (excluding current shop)
  const isSlugTaken = async (slug) => {
    const q = query(
      collection(firestore, "rentalShops"),
      where("slug", "==", slug)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.some((doc) => doc.id !== shopId);
  };

  // Handlers for operating hours
  const handleOperatingChange = (day, index, field, value) => {
    setProfile((prev) => {
      const newDaySlots = [...prev.operatingHours[day]];
      newDaySlots[index] = { ...newDaySlots[index], [field]: value };
      return {
        ...prev,
        operatingHours: {
          ...prev.operatingHours,
          [day]: newDaySlots,
        },
      };
    });
  };

  const handleAddTimeSlot = (day) => {
    setProfile((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: [...prev.operatingHours[day], { open: "", close: "" }],
      },
    }));
  };

  const handleRemoveTimeSlot = (day, index) => {
    setProfile((prev) => {
      const newDaySlots = prev.operatingHours[day].filter(
        (_, i) => i !== index
      );
      return {
        ...prev,
        operatingHours: {
          ...prev.operatingHours,
          [day]: newDaySlots,
        },
      };
    });
  };

  const handleBankDetailChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: value,
      },
    }));
  };

  // Save the profile: upload image if needed and update Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Start submitting edited data...");
    setUpdating(true);
    try {
      let imageUrl = profile.imageUrl;
      if (imageFile) {
        const storageRef = ref(
          storage,
          `shopImages/${profile.slug}/${imageFile.name}`
        );
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      const fullAddress = `${profile.address.street}, ${profile.address.city} ${profile.address.postalCode}, ${profile.address.country}`;

      console.log("update doc: ", profile.slug);
      await updateDoc(doc(firestore, "rentalShops", profile.slug), {
        ...profile,
        imageUrl,
        fullAddress,
        deliveryToHotel: profile.deliveryToHotel || {
          active: false,
          price: "",
        },
      });
      const bankDetailsRef = doc(
        firestore,
        "rentalShops",
        profile.slug,
        "private",
        "bankDetails"
      );
      await updateDoc(bankDetailsRef, {
        bankName: profile.bankDetails?.bankName || "",
        accountName: profile.bankDetails?.accountName || "",
        accountNumber: profile.bankDetails?.accountNumber || "",
      });
      setProfile((prev) => ({
        ...prev,
        imageUrl,
      }));
      setEditMode(false);
    } catch (error) {
      setUpdating(false);
      console.error("Error updating profile:", error);
      alert("There was an error updating the profile.");
    } finally {
      setUpdating(false);
    }
  };

  if (loadingAuth) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading authentication...</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading data...</Typography>
      </Container>
    );
  }

  if (updating) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Updating profile, please wait...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" align="left" gutterBottom>
        Shop Details
      </Typography>
      {!editMode ? (
        <Box>
          {profile.imageUrl && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">Shop Image:</Typography>
              <Box>
                <img
                  src={profile.imageUrl}
                  alt="Shop"
                  style={{ width: "100%", maxHeight: 300, objectFit: "cover" }}
                />
              </Box>
            </Paper>
          )}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Shop Name:</Typography>
            <Typography variant="body1">{profile.name || "N/A"}</Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Shop Address:</Typography>
            <Typography variant="body1">
              {profile.fullAddress ||
                `${profile.address.street}, ${profile.address.city} ${profile.address.postalCode}, ${profile.address.country}` ||
                "N/A"}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Shop Coordinates:</Typography>
            <Typography variant="body1">
              {profile.location.lat || "N/A"}, {profile.location.lng || "N/A"}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Custom URL (Slug):</Typography>
            <Typography variant="body1">
              ridecan.com/{profile.slug || "N/A"}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Description:</Typography>
            <Typography variant="body1">
              {profile.description || "No description provided."}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Phone Number:</Typography>
            <Typography variant="body1">{profile.phone || "N/A"}</Typography>
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Bank Details for Payout:</Typography>
            <Typography variant="body1">
              <strong>Bank Name:</strong>{" "}
              {profile.bankDetails?.bankName || "Not set"}
            </Typography>
            <Typography variant="body1">
              <strong>Account Name:</strong>{" "}
              {profile.bankDetails?.accountName || "Not set"}
            </Typography>
            <Typography variant="body1">
              <strong>Account Number:</strong>{" "}
              {profile.bankDetails?.accountNumber || "Not set"}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Operating Hours:</Typography>
            {daysOfWeek.map((day) => (
              <Box key={day} sx={{ mb: 1 }}>
                <Typography variant="subtitle1">{day}:</Typography>
                {profile.operatingHours[day] &&
                profile.operatingHours[day].length > 0 ? (
                  profile.operatingHours[day].map((slot, index) => (
                    <Typography variant="body2" key={index}>
                      {slot.open} - {slot.close}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2">Closed</Typography>
                )}
              </Box>
            ))}
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Extra Drop-off Locations:</Typography>
            {profile.dropOffLocations?.length > 0 ? (
              profile.dropOffLocations.map((loc, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography variant="body1">
                    <strong>{loc.locationName}</strong> - ₱{loc.price} <br />
                    {loc.address} <br />
                    Block after drop-off: {loc.blockingTimeAfterDropoff} hours
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2">
                No drop-off locations set.
              </Typography>
            )}
          </Paper>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Booking Rules:</Typography>
            <Typography variant="body2">
              Free Extra Hours: {profile.rules?.freeExtraHours ?? 0}
            </Typography>
            <Typography variant="body2">
              Minimum Rental Duration:{" "}
              {profile.rules?.minRentalDurationHours ?? 2} hours
            </Typography>
            <Typography variant="body2">
              Maximum Rental Duration:{" "}
              {profile.rules?.maxRentalDurationHours ?? "No limit"}
            </Typography>
            <Typography variant="body2">
              Buffer Time Before Booking:{" "}
              {profile.rules?.bufferTimeBeforeBookingHours ?? 0} hours
            </Typography>
            <Typography variant="body2">
              Same-Day Booking Cutoff:{" "}
              {profile.rules?.sameDayBookingCutoff ?? "18:00"}
            </Typography>
            <Typography variant="body2">
              Allow Same-Day Bookings:{" "}
              {profile.rules?.allowSameDayBookings ? "Yes" : "No"}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Delivery to Hotel</Typography>
            <Typography variant="body2">
              {profile.deliveryToHotel?.active
                ? `Service enabled - ₱${profile.deliveryToHotel.price}`
                : "Service not available"}
            </Typography>
          </Paper>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button variant="contained" onClick={() => setEditMode(true)}>
              Edit Profile
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shop Name"
                name="name"
                value={profile.name || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Shop Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street"
                name="street"
                value={profile.address.street}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={profile.address.city}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postalCode"
                value={profile.address.postalCode}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={profile.address.country}
                onChange={handleAddressChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Coordinates</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                name="lat"
                value={profile.location.lat}
                onChange={handleLocationChange}
                type="number"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                name="lng"
                value={profile.location.lng}
                onChange={handleLocationChange}
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shop Description"
                name="description"
                value={profile.description || ""}
                onChange={handleChange}
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={profile.phone || ""}
                onChange={handleChange}
                helperText="Include country code (e.g., +63)"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Bank Details for Payouts</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bankName"
                value={profile.bankDetails.bankName}
                onChange={handleBankDetailChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Name"
                name="accountName"
                value={profile.bankDetails.accountName}
                onChange={handleBankDetailChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Number"
                name="accountNumber"
                value={profile.bankDetails.accountNumber}
                onChange={handleBankDetailChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Booking Rules</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Free Extra Hours"
                type="number"
                fullWidth
                value={profile.rules?.freeExtraHours ?? 0}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    rules: {
                      ...prev.rules,
                      freeExtraHours: Number(e.target.value),
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Rental Duration (hours)"
                type="number"
                fullWidth
                value={profile.rules?.minRentalDurationHours ?? 2}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    rules: {
                      ...prev.rules,
                      minRentalDurationHours: Number(e.target.value),
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Maximum Rental Duration (hours)"
                type="number"
                fullWidth
                value={profile.rules?.maxRentalDurationHours ?? ""}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    rules: {
                      ...prev.rules,
                      maxRentalDurationHours: e.target.value
                        ? Number(e.target.value)
                        : null,
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Buffer Time Before Booking (hours)"
                type="number"
                fullWidth
                value={profile.rules?.bufferTimeBeforeBookingHours ?? 0}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    rules: {
                      ...prev.rules,
                      bufferTimeBeforeBookingHours: Number(e.target.value),
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Same-Day Booking Cutoff (HH:mm)"
                type="time"
                fullWidth
                value={profile.rules?.sameDayBookingCutoff ?? "18:00"}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    rules: {
                      ...prev.rules,
                      sameDayBookingCutoff: e.target.value,
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Delivery to Hotel</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delivery Price (₱)"
                type="number"
                fullWidth
                value={profile.deliveryToHotel?.price || ""}
                disabled={!profile.deliveryToHotel?.active}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    deliveryToHotel: {
                      ...prev.deliveryToHotel,
                      price: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant={
                  profile.deliveryToHotel?.active ? "outlined" : "contained"
                }
                color={profile.deliveryToHotel?.active ? "error" : "primary"}
                onClick={() =>
                  setProfile((prev) => ({
                    ...prev,
                    deliveryToHotel: {
                      ...prev.deliveryToHotel,
                      active: !prev.deliveryToHotel?.active,
                    },
                  }))
                }
                fullWidth
              >
                {profile.deliveryToHotel?.active
                  ? "Disable Delivery to Hotel"
                  : "Enable Delivery to Hotel"}
              </Button>
            </Grid>

            {/* Operating Hours - Editable Section */}
            <Grid item xs={12}>
              <Typography variant="h6">Operating Hours</Typography>
              {daysOfWeek.map((day) => (
                <Box key={day} sx={{ mb: 2, border: "1px solid #ccc", p: 1 }}>
                  <Typography variant="subtitle1">{day}</Typography>
                  {profile.operatingHours[day].map((slot, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <TextField
                        label="Open"
                        type="time"
                        name="open"
                        value={slot.open}
                        onChange={(e) =>
                          handleOperatingChange(
                            day,
                            index,
                            "open",
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Close"
                        type="time"
                        name="close"
                        value={slot.close}
                        onChange={(e) =>
                          handleOperatingChange(
                            day,
                            index,
                            "close",
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                      <IconButton
                        onClick={() => handleRemoveTimeSlot(day, index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    onClick={() => handleAddTimeSlot(day)}
                  >
                    Add Time Slot
                  </Button>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Drop-off Locations</Typography>
              {profile.dropOffLocations.map((location, index) => (
                <Box key={index} sx={{ border: "1px solid #ccc", p: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Location Name"
                    value={location.locationName}
                    onChange={(e) =>
                      handleDropOffChange(index, "locationName", e.target.value)
                    }
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    value={location.address}
                    onChange={(e) =>
                      handleDropOffChange(index, "address", e.target.value)
                    }
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (₱)"
                    type="number"
                    value={location.price}
                    onChange={(e) =>
                      handleDropOffChange(index, "price", e.target.value)
                    }
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Block Time After Dropoff (hours)"
                    type="number"
                    value={location.blockingTimeAfterDropoff}
                    onChange={(e) =>
                      handleDropOffChange(
                        index,
                        "blockingTimeAfterDropoff",
                        e.target.value
                      )
                    }
                    sx={{ mb: 1 }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveDropOff(index)}
                  >
                    Delete Location
                  </Button>
                </Box>
              ))}
              <Button variant="outlined" onClick={handleAddDropOff}>
                Add Drop-off Location
              </Button>
            </Grid>
            {/* Shop Image Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Shop Image</Typography>
              {profile.imageUrl && (
                <Box mb={2}>
                  <img
                    src={profile.imageUrl}
                    alt="Shop"
                    style={{
                      width: "100%",
                      maxHeight: 300,
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}
              <Button variant="contained" component="label">
                Upload New Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Grid>
            {/* Save and Cancel Buttons */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Save Profile
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setEditMode(false)}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default ShopProfile;
